import { defineMessages } from 'react-intl';

/**
 * Localized copy for desktop error codes that have no entry in the i18next
 * catalog and cannot gain one.
 *
 * `src/locales/en/common.json` is the deprecated i18next source and is frozen
 * against ADDITIONS by `scripts/check-common-json-additions.ts`: existing keys
 * may be edited or deleted for migration cleanup, but a new key fails CI. So a
 * NEW desktop error code cannot get copy the way the ~140 existing ones did. It
 * gets it here instead, as react-intl descriptors extracted into
 * `src/locales/en/messages.json` by the localisation pipeline.
 *
 * `translateDesktopError` reads this map BEFORE the i18next one, so a code
 * listed here is localized on every path that already routes through it —
 * `ipc.ts`'s `ERROR` and `WARNING` listeners, `ipc_invoke`'s rejection handler,
 * and the integration constants — for online and offline captures alike.
 *
 * The precedent is `sckCaptureFailureMessages` in `./ipc.messages.ts`, which
 * solved the same problem one code at a time inside the `ERROR` listener. This
 * moves the solution to the choke point so each new code is one entry rather
 * than another bespoke branch.
 *
 * These strings are desktop-only by construction: the codes are emitted by
 * native Rust producers over a Tauri event, so no browser ever raises them and
 * no browser-oriented remedy ("refresh the page") belongs in them.
 */
export const desktopErrorMessages = defineMessages({
  /**
   * PLACEHOLDER COPY — wording is a product decision and is not yet signed off.
   *
   * What the state actually is, which is what the wording has to convey: the
   * real-time audio callback could not hand blocks to the WAV writer fast
   * enough and `DataIntegrityMonitor` has declared a critical failure. Audio
   * already written to the file is intact and stays on disk; what is being lost
   * is the audio from this moment on. Nothing stops the capture — the producer
   * has no control loop that acts on this — so it keeps running and keeps
   * dropping samples until the clinician ends it.
   *
   * So the copy must say two things a generic "something went wrong" cannot:
   * the session so far is SAFE, and continuing makes it worse. "Try again" is
   * wrong here — it reads as "nothing was kept".
   */
  recordingFailed: {
    defaultMessage:
      'Audio is being lost from this session. Everything captured so far is saved — end transcribing now, then start a new session to avoid further gaps.',
    id: 'fCQy8jznll',
    description:
      'Destructive toast body when the audio pipeline starts dropping samples mid-session. The capture stays live and keeps losing audio, so ending it is the remedy; audio already captured is retained.',
  },
});

/**
 * Stand-ins for the i18next desktop error strings that send the user to Heidi
 * support, for a deployment that cannot reach it. Keyed by the i18next key they
 * replace, so the rest of the sentence stays in step with the original.
 */
export const desktopErrorContactItMessages = defineMessages({
  'desktop.errors.unknown': {
    defaultMessage: 'Something went wrong. Please try again or contact your IT team.',
    id: 'XU/LtYpNqt',
  },
  'desktop.errors.auth.configError': {
    defaultMessage:
      'Authentication is not configured correctly. Please contact your IT team.',
    id: 't/U4jdMNdd',
  },
  'desktop.errors.auth.configValidation': {
    defaultMessage:
      'Authentication configuration is invalid. Please contact your IT team.',
    id: 'RpKihxBEg5',
  },
});
